import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../App';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import TournamentReplay from './TournamentReplay';

const SEEN_KEY = 'seenResultStories';

const getSeen = () => {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY)) || []; } catch { return []; }
};

const ResultsStories = () => {
  const { API } = useContext(AuthContext);
  const [stories, setStories] = useState([]);
  const [activeIdx, setActiveIdx] = useState(null);
  const [seen, setSeen] = useState(getSeen);

  useEffect(() => {
    axios.get(`${API}/stories/recent`)
      .then((res) => setStories(res.data.stories || []))
      .catch(() => setStories([]));
  }, [API]);

  const markSeen = useCallback((tid) => {
    setSeen((prev) => {
      if (prev.includes(tid)) return prev;
      const next = [...prev, tid].slice(-30);
      localStorage.setItem(SEEN_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const openStory = (i) => {
    setActiveIdx(i);
    markSeen(stories[i].tournament.id);
  };

  const handleComplete = () => {
    const nextIdx = activeIdx + 1;
    if (nextIdx < stories.length) {
      markSeen(stories[nextIdx].tournament.id);
      setActiveIdx(nextIdx);
    } else {
      setActiveIdx(null);
    }
  };

  if (stories.length === 0) return null;

  const active = activeIdx !== null ? stories[activeIdx] : null;

  // מציאת שם האלוף לתצוגה בבועה
  const champOf = (s) => {
    const f = s.matches.find((m) => m.round === 'final');
    if (!f) return null;
    return f.winner_id === f.player1_id ? f.player1_name : f.player2_name;
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString('he-IL', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 pt-6" dir="rtl" data-testid="results-stories">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-lg">🎬</span>
        <h3 className="font-extrabold text-gray-800">סטורי תוצאות</h3>
        <span className="text-xs text-gray-400">התחרויות האחרונות שהסתיימו</span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-3" style={{ scrollbarWidth: 'thin' }}>
        {stories.map((s, i) => {
          const isSeen = seen.includes(s.tournament.id);
          const champ = champOf(s);
          const champAvatar = s.players.find((p) => p.full_name === champ)?.avatar;
          return (
            <button
              key={s.tournament.id}
              onClick={() => openStory(i)}
              data-testid={`story-bubble-${s.tournament.id}`}
              className="flex flex-col items-center gap-1.5 shrink-0 w-20 group"
            >
              <div className={`p-[3px] rounded-full transition-transform group-hover:scale-105 ${
                isSeen ? 'bg-gray-300' : 'bg-gradient-to-tr from-amber-400 via-orange-500 to-emerald-500'
              }`}>
                <div className="p-[2px] bg-white rounded-full">
                  <Avatar className="w-14 h-14">
                    {champAvatar && <AvatarImage src={champAvatar} alt={champ} />}
                    <AvatarFallback className="bg-slate-900 text-amber-300 text-xl">🏆</AvatarFallback>
                  </Avatar>
                </div>
              </div>
              <span className="text-[11px] font-bold text-gray-700 truncate w-full text-center leading-tight">
                {s.tournament.name}
              </span>
              <span className="text-[10px] text-gray-400 -mt-1">{fmtDate(s.tournament.date)}</span>
            </button>
          );
        })}
      </div>

      {active && (
        <TournamentReplay
          key={active.tournament.id}
          open
          condensed
          onClose={() => setActiveIdx(null)}
          onComplete={handleComplete}
          matches={active.matches}
          players={active.players}
          tournamentName={active.tournament.name}
          tournamentDate={active.tournament.date}
        />
      )}
    </div>
  );
};

export default ResultsStories;
